import React, { useEffect } from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { Modal, ModalBody, ModalHeader } from '@cfa/react-components';

import { useUpdatePlan } from '@/hooks/useUpdatePlan';

import { isApiError } from '@/utils/request';

import GenericError from '@/components/Error/GenericError';
import LoadingOverlay from '@/components/LoadingOverlay/LoadingOverlay';
import LoadMorePaginator from '@/components/LoadMorePaginator/LoadMorePaginator';
import NoMessage from '@/components/NoMessage/NoMessage';

import Constants from '@/constants';

import QuizLibraryCard from '../Cards/QuizLibraryCard';

import { getCustomQuizzes1Options } from '@/_generated/api-clients/content';
import { CustomQuizDto, Plan } from '@/_generated/api-clients/xp';
import { sourceDataFamily, useDataView } from '@/atoms/data-view';

interface QuizLibraryPopUpProps {
  isOpen: boolean;
  onClose: ({
    withPreview,
    quiz,
  }: {
    withPreview?: boolean;
    quiz?: CustomQuizDto;
  }) => void;
  plan?: Plan;
  sectionId: string;
}

const sourceData = sourceDataFamily('build-quizzes');

const QuizLibraryPopUp: React.FC<QuizLibraryPopUpProps> = ({
  isOpen,
  onClose,
  plan,
  sectionId,
}) => {
  const { t } = useTranslation();
  const { addSteps } = useUpdatePlan({
    onSuccess: () => {
      onClose({ withPreview: false });
    },
  });
  const [
    {
      display: { showing, total, items },
    },
    { showMore },
  ] = useDataView('build-quizzes');

  const {
    data: quizzes,
    error: errorGettingQuizzes,
    isFetching: isFetchingQuizzes,
  } = useQuery({
    ...getCustomQuizzes1Options(),
    enabled: isOpen,
  });

  const setSourceData = useSetAtom(sourceData);

  useEffect(() => {
    setSourceData(quizzes ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizzes]);

  if (isApiError(errorGettingQuizzes)) {
    datadogRum.addError(
      errorGettingQuizzes instanceof Error
        ? errorGettingQuizzes
        : new Error(String(errorGettingQuizzes)),
    );
    return <GenericError />;
  }

  const withinSection = !!sectionId.length;

  const getQuizStep = (quiz: CustomQuizDto) => ({
    id: uuidv4(),
    name: {
      en: quiz.name?.en ?? quiz.name?.es ?? '',
      es: quiz.name?.es ?? quiz.name?.en ?? '',
    },
    type: Constants.STEP_TYPES.QUIZ,
    quizId: quiz.typeFormId,
    reference: undefined,
    note: undefined,
    urls: [],
    available: true,
  });

  const onAddQuiz = async (quiz: CustomQuizDto) => {
    if (!plan) return;
    const quizStep = getQuizStep(quiz);
    await addSteps(plan, [quizStep], withinSection ? sectionId : undefined);
  };

  return (
    <>
      <LoadingOverlay isOpen={isFetchingQuizzes} />

      <Modal
        className="[&_.cfa-modal-header]:pb-8!"
        // @ts-expect-error onClose prop type mismatch with Modal component
        onClose={onClose}
        scrollMode="modal-body"
        show={isOpen}
        size="lg"
      >
        <ModalHeader>{t('TrainingPlans.addQuiz.title')}</ModalHeader>
        <ModalBody>
          <LoadingOverlay isOpen={isFetchingQuizzes} />
          {showing && !isFetchingQuizzes && (
            <>
              {items.map((quiz, index) => (
                <QuizLibraryCard
                  key={index}
                  onAddQuiz={onAddQuiz}
                  onPreviewQuiz={quizToPreview =>
                    onClose({ withPreview: true, quiz: quizToPreview })
                  }
                  quiz={quiz}
                  quizAddButtonTestId="QuizLibraryAddButton"
                  quizNameTestId="QuizLibraryName"
                />
              ))}
              <LoadMorePaginator
                onClick={() => showMore()}
                showing={showing}
                showingText={t('TrainingPlans.showingXOfYQuizzes', {
                  showing,
                  total,
                })}
                total={total}
              />
            </>
          )}
          {!showing && !isFetchingQuizzes && (
            <NoMessage
              message={t('TrainingPlans.buildQuizzes.noQuizzesPopup')}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default QuizLibraryPopUp;
